import {Avatar, Image, message} from 'antd'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import getAvatar from 'helpers/getAvatar'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import UserContext from '../UserContext'
import {FollowButton} from '../../../../components/Feed/FollowButton'
import {renderIf} from '../../../Shared'
import classNames from 'classnames'
import {PhotoPlaceholderUser} from '../../../../conts/imageBase64'
import {PiChats, PiLinkSimpleBold} from 'react-icons/pi'
import {GoShieldLock} from 'react-icons/go'
import {HeaderButton} from '../../../Organization/components/PageHeader'
import copyToClipboard from 'copy-to-clipboard'
import {UserShareWidget} from '../../factory/createShareWidget'
import {ROOT_URL} from '../../../../envs/_current/config'
import getLinkToDetail from 'helpers/getLinkToDetail'
import TooltipApp from 'components/TooltipApp'
import {RiUser2Fill} from 'react-icons/ri'
import {ConversationWith} from '../../factory/createChatButton'
import {getId, getType} from '../../../../apis/model/base'
import getAliasName from '../../../../helpers/getAliasName'
import {usePromoFunc} from '../../../Shared/functions/usePromoFunc'
import {BsThreeDots} from 'react-icons/bs'
import {TbHeartCheck, TbHeartX} from 'react-icons/tb'
import DrawMenu from '../../../../components/drawer/DrawMenu'
import {SystemPermission} from '../../../../components/RequiredAdmin'
import {LoginContext} from '../../../../components/LoginContext'

const BannerSkeleton = ({isSm}) => (
    <div className="flex flex-col">
        <div
            className="w-full background-200"
            style={{height: isSm ? 150 : 180}}
        />
        <div
            className="w-full max-w-6xl mx-auto px-3 md:px-0 flex items-end gap-3"
            style={{marginTop: -30}}>
            <div
                className="rounded-full background-200 border-4 border-color-50 flex-shrink-0"
                style={{width: isSm ? 76 : 100, height: isSm ? 76 : 100}}
            />
            <div className="mb-3 flex flex-col gap-2 flex-1">
                <div className="h-5 w-40 background-200 rounded"/>
                <div className="h-3 w-28 background-200 rounded"/>
            </div>
        </div>
    </div>
)

const UserBanner = React.memo(({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {user} = useContext(UserContext)
    const {isSm} = useContext(LayoutContext)

    if (!user) return <BannerSkeleton isSm={isSm}/>

    const {name, email, certified, patron_saint_name} = user ?? {}
    const shareUrl = `${ROOT_URL}${getLinkToDetail(user)}`

    const {onAddToRecommendation, onRemoveFromRecommendation} = usePromoFunc({})

    const login = useContext(LoginContext)
    const it_me = user?.id === login?.id

    return (
        <div className="flex flex-col relative">
            <Image
                preview={false}
                src={_.get(user, 'cover_photo') || PhotoPlaceholderUser}
                wrapperClassName="w-full"
                className="fadeIn animated object-cover"
                style={{height: isSm ? 150 : 180}}
            />

            <div
                style={{marginTop: isSm ? -30 : -25}}
                className="w-full max-w-6xl mx-auto px-3 md:px-0 flex items-end gap-2 md:gap-4">
                <Avatar
                    size={isSm ? 76 : 100}
                    style={{border: '4px solid #fff', flexShrink: 0}}
                    icon={<RiUser2Fill size={isSm ? 38 : 60} className="text-color-500"/>}
                    src={getAvatar(user)}
                    className="flex flex-center background-200"
                />
                <div className="flex items-center flex-1 gap-3 min-w-0">
                    <div
                        className={classNames(
                            'flex items-center gap-2 md:gap-8 flex-1 min-w-0',
                            isSm ? 'mb-2' : 'mb-4 mr-3 md:mr-0'
                        )}>
                        <div className={classNames('flex flex-col min-w-0', isSm && 'flex-1')}>
                            <span className="font-extrabold text-lg md:text-xl text-color-000 leading-snug uppercase tracking-wide truncate">
                                <span className="mr-1 font-medium text-base text-color-400 italic capitalize">
                                    {getAliasName(user, 'patron_saint') || ''}
                                </span>
                                {name}
                            </span>
                            {renderIf(
                                !isSm && email,
                                <span className="text-xs text-color-400 italic tracking-wider">{email}</span>
                            )}
                        </div>
                        {renderIf(
                            certified === 1,
                            <TooltipApp title={t('verified')} classNames={{root: 'font-medium text-xs italic'}}>
                                <GoShieldLock
                                    size={isSm ? 18 : 24}
                                    className="text-green-300 zoom-animation cursor-pointer flex-shrink-0"
                                />
                            </TooltipApp>
                        )}
                    </div>

                    {renderIf(
                        !isSm,
                        <div className="flex flex-col items-end gap-3 pr-3">
                            <div className="flex items-center gap-x-3">
                                {renderIf(
                                    !it_me,
                                    <FollowButton item={user} size="medium"/>
                                )}
                                {renderIf(
                                    !it_me && user?.follow_status === true,
                                    <ConversationWith
                                        params={{receiver_id: getId(user)}}
                                        render={(__, {onClick}) => (
                                            <div
                                                onClick={onClick}
                                                className="px-3 flex flex-center gap-x-2 bg-blue-100 rounded-md font-medium text-blue-500 hover:text-blue-700 cursor-pointer"
                                                style={{height: 28, fontSize: 14}}>
                                                {t('send message')}
                                                <PiChats size={14}/>
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                            <UserShareWidget
                                className="gap-3"
                                footer={
                                    <div className="flex gap-3">
                                        <HeaderButton
                                            onClick={() => {
                                                copyToClipboard(shareUrl)
                                                message.success(t('copied'))
                                            }}
                                            width={24}
                                            height={24}
                                            icon={
                                                <TooltipApp
                                                    title={t('copy link')}
                                                    placement="bottomRight"
                                                    overlayClassName="text-xs italic">
                                                    <PiLinkSimpleBold size={12}/>
                                                </TooltipApp>
                                            }
                                        />
                                        <SystemPermission system={true}>
                                            {(isAdmin) => renderIf(
                                                isAdmin,
                                                <DrawMenu
                                                    menu={{
                                                        items: [
                                                            {
                                                                key: 'ADD_TO_RECOMMENDATION',
                                                                icon: <TbHeartCheck className="text-color-400"/>,
                                                                label: (
                                                                    <span className="font-medium text-sm text-color-100">
                                                                        {t('add to recommendation')}
                                                                    </span>
                                                                ),
                                                            },
                                                            {
                                                                key: 'REMOVE_FROM_RECOMMENDATION',
                                                                icon: <TbHeartX className="text-color-400"/>,
                                                                label: (
                                                                    <span className="font-medium text-sm text-color-100">
                                                                        {t('remove from recommendation')}
                                                                    </span>
                                                                ),
                                                            },
                                                        ],
                                                        onClick: ({key}) => {
                                                            switch (key) {
                                                                case 'ADD_TO_RECOMMENDATION':
                                                                    onAddToRecommendation({
                                                                        owner_type: getType(user),
                                                                        owner_id: getId(user),
                                                                    })
                                                                    break
                                                                case 'REMOVE_FROM_RECOMMENDATION':
                                                                    onRemoveFromRecommendation({
                                                                        owner_type: getType(user),
                                                                        owner_id: getId(user),
                                                                    })
                                                                    break
                                                                default:
                                                                    break
                                                            }
                                                        },
                                                    }}
                                                    menuPlacement="bottomLeft"
                                                    component={
                                                        <HeaderButton width={24} height={24} icon={<BsThreeDots size={12}/>}/>
                                                    }
                                                />
                                            )}
                                        </SystemPermission>
                                    </div>
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
})

export default UserBanner
